import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  archiveAdCode,
  createAdCode,
  getAdCode,
  listAdCodeAttributions,
  listAdCodeMedia,
  listAdCodes,
  updateAdCode,
} from 'api/adCodes'
import { queryKeys } from 'constants/queryKeys'
import { adCodeStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import type {
  TAdCodeAttributionsResponse,
  TAdCodeDetail,
  TAdCodeListResponse,
  TListAdCodesRequest,
  TUpsertAdCodeRequest,
} from 'types'
import { toErrorMessage } from './utils'

export function useAdCodeMedia() {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: [queryKeys.AD_CODES, 'media', user?.uid],
    enabled: Boolean(user?.uid),
    queryFn: () => listAdCodeMedia(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return {
    media: query.data ?? [],
    loading: Boolean(user?.uid) && query.isPending,
    error: query.error
      ? toErrorMessage(query.error, adCodeStrings.errors.loadMediaFailed)
      : undefined,
    refetch: query.refetch,
  }
}

export function useAdCodes(params: TListAdCodesRequest) {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: [queryKeys.AD_CODES, user?.uid, params],
    enabled: Boolean(user?.uid),
    queryFn: () => listAdCodes(params),
    retry: false,
  })

  return {
    data: query.data as TAdCodeListResponse | undefined,
    loading: Boolean(user?.uid) && (query.isPending || query.isFetching),
    error: query.error
      ? toErrorMessage(query.error, adCodeStrings.errors.loadFailed)
      : undefined,
    refetch: query.refetch,
  }
}

export function useAdCode(
  adCodeId: number | undefined,
  params: Pick<
    TListAdCodesRequest,
    'from' | 'to' | 'excludeBlocked24h' | 'metrics'
  > = {},
) {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: [queryKeys.AD_CODES, 'detail', user?.uid, adCodeId, params],
    enabled: Boolean(user?.uid) && Number.isInteger(adCodeId),
    queryFn: () => getAdCode(adCodeId as number, params),
    // 期間を切り替えた瞬間に詳細ページ全体が読み込み表示へ戻らないよう、
    // 前回の取得結果を出したまま指標だけ差し替える
    placeholderData: (previous: TAdCodeDetail | undefined) => previous,
    retry: false,
  })

  return {
    adCode: query.data as TAdCodeDetail | undefined,
    loading:
      Boolean(user?.uid) &&
      Number.isInteger(adCodeId) &&
      (query.isPending || query.isFetching),
    error: query.error
      ? toErrorMessage(query.error, adCodeStrings.errors.loadFailed)
      : undefined,
    refetch: query.refetch,
  }
}

export function useAdCodeAttributions(
  adCodeId: number | undefined,
  page: number,
  params: Pick<TListAdCodesRequest, 'from' | 'to'> = {},
) {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: [
      queryKeys.AD_CODES,
      'attributions',
      user?.uid,
      adCodeId,
      page,
      params,
    ],
    enabled: Boolean(user?.uid) && Number.isInteger(adCodeId),
    queryFn: () => listAdCodeAttributions(adCodeId as number, page, params),
    retry: false,
  })

  return {
    data: query.data as TAdCodeAttributionsResponse | undefined,
    loading:
      Boolean(user?.uid) &&
      Number.isInteger(adCodeId) &&
      (query.isPending || query.isFetching),
    error: query.error
      ? toErrorMessage(query.error, adCodeStrings.errors.loadAttributionsFailed)
      : undefined,
  }
}

/** 作成・更新・アーカイブをまとめて扱う。成功時は一覧と詳細を取り直す。 */
export function useAdCodeMutations() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const onSaved = (updated: TAdCodeDetail) => {
    // 保存した内容はすぐ反映し、全期間の応答で期間別の指標を上書きしない。
    queryClient.setQueriesData<TAdCodeDetail>(
      { queryKey: [queryKeys.AD_CODES, 'detail', user?.uid, updated.id] },
      (previous) =>
        previous ? { ...updated, metrics: previous.metrics } : previous,
    )
    // 再取得は続けるが、成功通知と画面遷移は保存APIの成功時に進める。
    void queryClient.invalidateQueries({ queryKey: [queryKeys.AD_CODES] })
  }

  const create = useMutation({
    mutationFn: (request: TUpsertAdCodeRequest) => createAdCode(request),
    onSuccess: onSaved,
  })
  const update = useMutation({
    mutationFn: ({
      adCodeId,
      request,
    }: {
      adCodeId: number
      request: TUpsertAdCodeRequest
    }) => updateAdCode(adCodeId, request),
    onSuccess: onSaved,
  })
  const archive = useMutation({
    mutationFn: ({
      adCodeId,
      archived,
    }: {
      adCodeId: number
      archived: boolean
    }) => archiveAdCode(adCodeId, archived),
    onSuccess: onSaved,
  })

  return {
    create,
    update,
    archive,
    saving: create.isPending || update.isPending || archive.isPending,
  }
}

/**
 * 絞り込みの選択肢に使う広告コードの一覧。
 * 数値は要らないので指標は見ず、名前とIDだけを使う。
 *
 * アーカイブ済みも含める。友だち一覧の「流入元」列は状態に関わらず管理名を出すので、
 * 候補から外すと「表に出ている名前で絞り込めない」ことになる（選択済みでも消える）。
 */
export function useAdCodeOptions(): Array<{ value: number; label: string }> {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: [queryKeys.AD_CODES, 'options', user?.uid],
    enabled: Boolean(user?.uid),
    // metrics: false で指標の集計は省く。それでも友だち一覧を開くたびに
    // 取り直す必要はないので、しばらくキャッシュする。
    staleTime: 5 * 60 * 1000,
    queryFn: () => listAdCodes({ pageSize: 100, metrics: false }),
    retry: false,
  })

  // 並びはバックエンドの orderBy が有効を先に返す。アーカイブ済みはラベルで見分ける
  return (query.data?.items ?? []).map((item) => ({
    value: item.id,
    label:
      item.status === 'ARCHIVED'
        ? `${item.name}（${adCodeStrings.status.ARCHIVED}）`
        : item.name,
  }))
}
